import { useState, useEffect } from "react";

// Third Party
import { Formik } from "formik";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";

// UI
import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbLink,
	BreadcrumbList,
	BreadcrumbPage,
	BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";

import { Separator } from "@/components/ui/separator";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import FroalaEditor from "@/components/FloaraEditor";

import { useToast } from "@/components/ui/use-toast";

// Icons
import { ArrowLeft, Upload } from "lucide-react";

// Services
import { createNew, updateNew } from "@/services/news";

// Types
import type { News, MutateNews } from "@/modules/news/types";

export const Route = createFileRoute("/_layout-private/news/mutate")({
	component: Mutate,
	validateSearch: (search: Record<string, unknown>) => {
		return {
			id: search.id as string | undefined,
			data: search.data as string | undefined,
		};
	},
});

function Mutate() {
	const { id, data } = Route.useSearch();

	const navigate = useNavigate();
	const { toast } = useToast();
	const queryClient = useQueryClient();

	const [parseData, setParseData] = useState({} as News);
	const [selectedFile, setSelectedFile] = useState<File>();

	useEffect(() => {
		if (data) {
			console.log(JSON.parse(data), "Data");
			setParseData(JSON.parse(data));
		}
	}, [data]);

	// Functions
	const createNewMutation = useMutation<unknown, unknown, MutateNews>({
		mutationFn: (formData) => createNew(formData),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["news"] });
			localStorage.removeItem("floaraSavedContent");
			toast({
				title: "ສຳເລັດ",
				description: "ເພີ່ມຂໍ້ມູນຂ່າວສານສໍາເລັດແລ້ວ",
			});
			navigate({ to: "/news", replace: true });
		},
	});

	const updateNewMutation = useMutation<unknown, unknown, MutateNews>({
		mutationFn: (formData) => updateNew(id, formData),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["news"] });
			localStorage.removeItem("floaraSavedContent");

			toast({
				title: "ສຳເລັດ",
				description: "ແກ້ໄຂຂໍ້ມູນຂ່າວສານສໍາເລັດແລ້ວ",
			});
			navigate({ to: "/news", replace: true });
		},
	});

	const mutateLoading =
		createNewMutation.isPending || updateNewMutation.isPending;

	return (
		<>
			<Breadcrumb>
				<BreadcrumbList>
					<BreadcrumbItem>
						<BreadcrumbLink>
							<Link replace to="/news">
								ຈັດການຂໍ້ມູນຂ່າວສານ
							</Link>
						</BreadcrumbLink>
					</BreadcrumbItem>
					<BreadcrumbSeparator />

					<BreadcrumbItem>
						<BreadcrumbPage>ເພີ່ມຂໍ້ມູນຂ່າວສານ</BreadcrumbPage>
					</BreadcrumbItem>
				</BreadcrumbList>
			</Breadcrumb>
			<section className="mt-[32px]">
				<h2 className="text-2xl font-semibold">ເພີ່ມຂໍ້ມູນຂ່າວສານ</h2>
				<p className="text-muted-foreground">ປ້ອນຂໍ້ມູນຂ່າວສານ</p>
				<Separator className="my-6" />
				<button
					type="button"
					onClick={() =>
						navigate({
							to: "/news",
							replace: true,
						})
					}
					className="flex items-center gap-[4px]"
				>
					<ArrowLeft className="w-5 h-5" />
					ກັບຄືນ
				</button>
			</section>
			<Formik
				enableReinitialize
				initialValues={{
					title: parseData?.title || "",
					description:
						localStorage.getItem("floaraSavedContent") ||
						parseData?.description ||
						"",
					info_type_id: parseData?.info_type_id || "",
				}}
				onSubmit={(values: MutateNews) => {
					id
						? updateNewMutation.mutate(values)
						: createNewMutation.mutate(values);
				}}
			>
				{({ values, handleChange, handleSubmit, setFieldValue }) => (
					<form
						onSubmit={handleSubmit}
						className="max-w-[628px] mx-auto mt-6 space-y-6"
					>
						<section className="space-y-6">
							<header>
								<h3 className="text-[18px] font-semibold">ຂໍ້ມູນຂ່າວສານ</h3>
								<p className="text-muted-foreground text-[14px]">
									ສິ່ງທີ່ທ່ານປ້ອນໄປຈະຖືກນຳໄປບັນທຶກເປັນຂໍ້ມູນຂ່າວສານ
								</p>
								<Separator className="mt-6" />
							</header>
							<div>
								<Label htmlFor="profile_image_url">ຮູບພາບ</Label>
								<div className="w-full h-[300px]  border border-dotted bg-zinc-50 border-zinc-200 overflow-hidden  relative flex items-center flex-col justify-center gap-[24px]">
									{selectedFile ? (
										<img
											alt="pre "
											src={URL.createObjectURL(selectedFile)}
											className="object-cover w-full h-full"
										/>
									) : (
										<>
											<Upload className="w-8 h-8 " />
											<div className="text-center text-[14px]">
												<p>ອັບໂຫຼດຮູບພາບ</p>
												<p className="w-full text-muted-foreground text-wrap">
													(ຂະໜາດບໍ່ເກີນ 10MB)
												</p>
											</div>{" "}
										</>
									)}

									<input
										type="file"
										name="background_url"
										id="background_url"
										accept="image/png, image/jpg, image/jpeg"
										className="absolute inset-0 opacity-0 cursor-pointer"
										onChange={(e) => {
											if (e.target.files) {
												setSelectedFile(e.target.files[0]);
											}
										}}
									/>
								</div>
							</div>
							<div>
								<Label htmlFor="title">
									ຫົວຂໍ້ຂ່າວ <span className="text-red-500">*</span>
								</Label>
								<Input
									type="text"
									placeholder="ປ້ອນຫົວຂໍ້ຂ່າວ..."
									className="w-full"
									required
									name="title"
									value={values.title}
									onChange={handleChange}
								/>
							</div>

							<div>
								<Label htmlFor="info_type_id">
									ປະເພດຂໍ້ມູນຂ່າວສານ <span className="text-red-500">*</span>
								</Label>
								<Select
									name="info_type_id"
									onValueChange={(value) =>
										setFieldValue("info_type_id", value)
									}
								>
									<SelectTrigger className="w-full">
										<SelectValue placeholder="ເລຶອກຕາມສິດການນຳໃຊ້" />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="all">ທັງໝົດ</SelectItem>
										<SelectItem value="apple">Apple</SelectItem>
									</SelectContent>
								</Select>
							</div>
							<div>
								<Label htmlFor="description">
									ລາຍລະອຽດ <span className="text-red-500">*</span>
								</Label>
								{/* <Field name="description" component={CKEditorComponent} /> */}
								<FroalaEditor
									value={values.description}
									onChange={(value) => setFieldValue("description", value)}
								/>
							</div>
						</section>

						<div className="flex justify-end w-full">
							<Button type="submit" disabled={mutateLoading}>
								{mutateLoading ? "ກຳລັງບັນທຶກຂໍ້ມູນ" : "ບັນທຶກ"}
							</Button>
						</div>
					</form>
				)}
			</Formik>
		</>
	);
}
